import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-label", "borderRadius", "children", "gap", "hasLabel", "limit", "size", "value", "variant"],
  _excluded2 = ["as", "fontSize", "isHeading"];
import styled from 'styled-components';
import FormattedPercentage from 'I18n/components/FormattedPercentage';
import { LayoutSpaceVertical } from '../Layout/LayoutSpaceVertical';
import { LayoutHorizontal } from '../Layout/LayoutHorizontal';
import { LayoutBox } from '../Layout/LayoutBox';
import { getOverLimit, getPercentage } from './utils';
import { createContext, useContext } from 'react';
import { Heading } from '../Typography/Heading';
import { Body } from '../Typography/Body';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ProgressBarContext = /*#__PURE__*/createContext({
  displayPercentage: 0
});
const FULL_PERCENT = 100;
const sizeMap = {
  xs: '0.3125rem',
  sm: '0.5rem',
  md: '0.75rem',
  lg: '1rem'
};
const variantMap = {
  success: 'var(--layout-lib-gradient-success)',
  warning: 'var(--layout-lib-gradient-warning)',
  danger: 'var(--layout-lib-gradient-danger)',
  breeze: 'var(--layout-lib-gradient-breeze-medium)'
};
const StyledBar = styled(LayoutBox).withConfig({
  displayName: "ProgressBar__StyledBar",
  componentId: "sc-17pf191-0"
})(["background:", ";color:var(--layout-lib-text-color-inverse);line-height:1.25;font-size:", ";text-align:center;transition:inline-size 0.3s ease-in-out;"], ({
  $variant
}) => variantMap[$variant], ({
  theme
}) => theme.typography['detail-100'].fontSize);
const PercentageLabel = ({
  value
}) => /*#__PURE__*/_jsx(FormattedPercentage, {
  value: value
});
export const ProgressBar = _ref => {
  let {
      'aria-label': ariaLabel,
      borderRadius = 'border-radius',
      children,
      gap = 'space-xs',
      hasLabel = false,
      limit,
      size = 'lg',
      value,
      variant = 'success'
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const percentage = getPercentage({
    value,
    limit
  });
  const isOverLimit = getOverLimit({
    value,
    limit
  });
  const displayPercentage = isOverLimit ? FULL_PERCENT : percentage;
  return /*#__PURE__*/_jsx(ProgressBarContext.Provider, {
    value: {
      displayPercentage
    },
    children: /*#__PURE__*/_jsxs(LayoutSpaceVertical, Object.assign({}, rest, {
      gap: gap,
      children: [/*#__PURE__*/_jsx(LayoutHorizontal, {
        justifyContent: "space-between",
        children: children
      }), /*#__PURE__*/_jsx(LayoutBox, {
        background: "background-color",
        blockSize: sizeMap[size],
        borderRadius: borderRadius,
        children: /*#__PURE__*/_jsx(StyledBar, {
          $size: size,
          $variant: variant,
          "aria-label": ariaLabel,
          "aria-live": "polite",
          "aria-valuemax": limit,
          "aria-valuemin": 0,
          "aria-valuenow": displayPercentage,
          blockSize: "100%",
          borderRadius: borderRadius,
          inlineSize: `${displayPercentage}%`,
          role: "progressbar",
          children: hasLabel ? /*#__PURE__*/_jsx(PercentageLabel, {
            value: displayPercentage
          }) : null
        })
      })]
    }))
  });
};
const PercentageWrapper = ({
  children
}) => {
  return /*#__PURE__*/_jsx(LayoutBox, {
    flex: "1 1 100%",
    textAlign: "right",
    children: children
  });
};
const ProgressBarPercentage = _ref2 => {
  let {
      as = 'div',
      fontSize = 'xs',
      isHeading = true
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const {
    displayPercentage
  } = useContext(ProgressBarContext);
  if (isHeading) {
    return /*#__PURE__*/_jsx(PercentageWrapper, {
      children: /*#__PURE__*/_jsx(Heading, Object.assign({}, rest, {
        as: as,
        fontSize: fontSize,
        children: /*#__PURE__*/_jsx(FormattedPercentage, {
          value: displayPercentage
        })
      }))
    });
  }
  return /*#__PURE__*/_jsx(PercentageWrapper, {
    children: /*#__PURE__*/_jsx(Body, Object.assign({}, rest, {
      as: as,
      fontSize: fontSize,
      children: /*#__PURE__*/_jsx(FormattedPercentage, {
        value: displayPercentage
      })
    }))
  });
};
ProgressBar.Percentage = ProgressBarPercentage;